import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { DialogFooter } from '@/components/ui/dialog';
import { Textarea } from '@/components/ui/textarea';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { useFetchAccounts } from '@accountHooks';
import { useRecordItemPayment } from '@/hooks/useLegalFeeStages';
import { itemPaymentFormSchema, type ItemPaymentFormData } from './legalFees.schema';
import { formatCurrency } from '@/lib/currency.utils';
import type { LegalFeeItem } from '@/types/legalFees';

interface ItemPaymentFormProps {
  item: LegalFeeItem;
  stageName: string;
  fixedAssetId: string;
  propertyName: string;
  onSuccess: () => void;
}

export function ItemPaymentForm({
  item,
  stageName,
  fixedAssetId,
  propertyName,
  onSuccess,
}: ItemPaymentFormProps) {
  const { data: accounts = [], isLoading: accountsLoading } = useFetchAccounts();
  const recordItemPayment = useRecordItemPayment();

  const today = new Date().toISOString().split('T')[0];

  const {
    register,
    handleSubmit,
    watch,
    setValue,
    formState: { errors, isSubmitting },
  } = useForm<ItemPaymentFormData>({
    resolver: zodResolver(itemPaymentFormSchema),
    defaultValues: {
      account_id: '',
      amount: item.amount,
      payment_date: today,
      notes: '',
    },
  });

  const accountId = watch('account_id');
  const selectedAccount = accounts.find((a) => a.id === accountId);

  const onSubmit = async (data: ItemPaymentFormData) => {
    try {
      await recordItemPayment.mutateAsync({
        itemId: item.id,
        fixedAssetId,
        accountId: data.account_id,
        amount: data.amount,
        paymentDate: data.payment_date,
        notes: data.notes ?? null,
        paymentCurrency: item.currency,
        stageName,
        itemTitle: item.title,
        propertyName,
      });
      toast.success('Payment recorded successfully');
      onSuccess();
    } catch (error: any) {
      // Surface InsufficientBalanceError and other errors via toast
      toast.error(error?.message ?? 'Failed to record payment');
    }
  };

  return (
    <form onSubmit={handleSubmit(onSubmit)} className="space-y-4">
      {/* Pre-filled amount and currency */}
      <div className="grid grid-cols-2 gap-4">
        <div className="space-y-2">
          <Label htmlFor="item-payment-amount">Amount *</Label>
          <Input
            id="item-payment-amount"
            type="number"
            step="0.01"
            min="0.01"
            {...register('amount', { valueAsNumber: true })}
            aria-invalid={!!errors.amount}
            aria-describedby={errors.amount ? 'item-payment-amount-error' : undefined}
          />
          {errors.amount && (
            <p id="item-payment-amount-error" className="text-sm text-destructive" role="alert">
              {errors.amount.message}
            </p>
          )}
        </div>
        <div className="space-y-2">
          <Label>Currency</Label>
          <Input value={item.currency} readOnly disabled />
        </div>
      </div>

      {/* Account selector */}
      <div className="space-y-2">
        <Label htmlFor="item-payment-account">Debit Account *</Label>
        <Select
          value={accountId}
          onValueChange={(val) => setValue('account_id', val)}
          disabled={accountsLoading}
        >
          <SelectTrigger
            id="item-payment-account"
            aria-invalid={!!errors.account_id}
            aria-describedby={errors.account_id ? 'item-payment-account-error' : undefined}
          >
            <SelectValue placeholder="Select an account" />
          </SelectTrigger>
          <SelectContent>
            {accounts.map((acc) => (
              <SelectItem key={acc.id} value={acc.id}>
                {acc.nickname || acc.name} ({acc.currency})
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
        {errors.account_id && (
          <p id="item-payment-account-error" className="text-sm text-destructive" role="alert">
            {errors.account_id.message}
          </p>
        )}
        {selectedAccount && (
          <p className="text-xs text-muted-foreground">
            Balance: {formatCurrency(selectedAccount.balance, selectedAccount.currency)}
          </p>
        )}
      </div>

      {/* Date picker — no future dates */}
      <div className="space-y-2">
        <Label htmlFor="item-payment-date">Payment Date *</Label>
        <Input
          id="item-payment-date"
          type="date"
          max={today}
          {...register('payment_date')}
          aria-invalid={!!errors.payment_date}
          aria-describedby={errors.payment_date ? 'item-payment-date-error' : undefined}
        />
        {errors.payment_date && (
          <p id="item-payment-date-error" className="text-sm text-destructive" role="alert">
            {errors.payment_date.message}
          </p>
        )}
      </div>

      {/* Notes */}
      <div className="space-y-2">
        <Label htmlFor="item-payment-notes">Notes (Optional)</Label>
        <Textarea
          id="item-payment-notes"
          {...register('notes')}
          placeholder="Optional notes…"
          rows={2}
          maxLength={500}
        />
        {errors.notes && (
          <p className="text-sm text-destructive" role="alert">
            {errors.notes.message}
          </p>
        )}
      </div>

      <DialogFooter>
        <Button type="submit" disabled={isSubmitting || recordItemPayment.isPending}>
          {isSubmitting || recordItemPayment.isPending ? 'Processing…' : 'Record Payment'}
        </Button>
      </DialogFooter>
    </form>
  );
}
